/**
 * PLT-4839: module-level singleton state for the `/chat` history sidebar.
 *
 * Mirrors the same pub/sub pattern as `chat-panel-state.ts` — the sidebar
 * subscribes via `useSyncExternalStore`, components that don't render the
 * sidebar never read this state, and the singleton has zero cost
 * elsewhere. Kept in a plain `.ts` file (no React imports) so it can be
 * read from any module — including the chat adapter when we want to know
 * "which conversation is currently active" without dragging a runtime ref
 * around.
 *
 * Two pieces of state live here:
 *
 *   1. `collapsed` — whether the sidebar is in icon-rail mode. Persisted
 *      to localStorage so the user's preference survives refresh /
 *      cross-tab. localStorage is per-browser; cross-device sync is out
 *      of scope for v1 (the sidebar is purely a UI affordance, not user
 *      data).
 *
 *   2. `activeConversationId` — the conversation the user is currently
 *      viewing. Updated by the chat composer when a turn starts (the
 *      thread's `composerState.threadId` is the conversation id), and
 *      cleared by `New chat`. The active row is highlighted in the list
 *      using this id.
 */

const STORAGE_KEY_COLLAPSED = 'upright.chat.history.collapsed';

export interface ChatHistoryState {
  /** Whether the sidebar is currently collapsed to its icon rail. */
  collapsed: boolean;
  /**
   * Id of the conversation currently visible in the chat surface. Mirrors
   * the chat composer's `threadId` whenever a thread exists; `null` while
   * the user is on a fresh empty `New chat` (no row should be highlighted).
   */
  activeConversationId: string | null;
}

let _state: ChatHistoryState = {
  collapsed: readCollapsedFromStorage(),
  activeConversationId: null,
};

const _listeners = new Set<() => void>();

function notify(): void {
  for (const l of _listeners) l();
}

/**
 * Snapshot accessor for `useSyncExternalStore`. Reference-stable between
 * mutations so React only re-renders when something actually changed —
 * each mutation builds a new `_state` object, identity comparison just
 * works.
 */
export function getChatHistoryState(): ChatHistoryState {
  return _state;
}

export function subscribeChatHistoryState(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Mutators
// ---------------------------------------------------------------------------

/**
 * Toggle the collapsed flag. Persists to localStorage so the choice
 * survives refresh. Multiple tabs each maintain their own copy — we
 * don't subscribe to the `storage` event because the UX of a sidebar
 * collapsing under you in tab B because you toggled it in tab A is more
 * surprising than useful.
 */
export function setChatHistoryCollapsed(collapsed: boolean): void {
  if (_state.collapsed === collapsed) return;
  _state = { ..._state, collapsed };
  writeCollapsedToStorage(collapsed);
  notify();
}

export function toggleChatHistoryCollapsed(): void {
  setChatHistoryCollapsed(!_state.collapsed);
}

/**
 * Mark a conversation as currently active. Pass `null` to clear (e.g. on
 * `New chat` before the first user message is sent). Same-value writes
 * are dropped so subscribers don't re-render when nothing changes.
 */
export function setActiveConversationId(id: string | null): void {
  if (_state.activeConversationId === id) return;
  _state = { ..._state, activeConversationId: id };
  notify();
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function readCollapsedFromStorage(): boolean {
  // SSR-safe — `typeof window === 'undefined'` covers Vite's SSR build
  // step plus any unit-test harness that runs without a DOM. Default to
  // expanded on first-ever load (matches Claude / ChatGPT behaviour).
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY_COLLAPSED) === '1';
  } catch {
    // Private-mode Safari throws on `localStorage.getItem` — silently
    // fall back to "expanded". The user just won't have persistence in
    // that browsing context, which is the correct behaviour.
    return false;
  }
}

function writeCollapsedToStorage(collapsed: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY_COLLAPSED, collapsed ? '1' : '0');
  } catch {
    // Same rationale as the read side — swallow quota / private-mode
    // exceptions so the runtime state still flips even if persistence
    // is unavailable.
  }
}
